import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Input, Textarea, Select } from '@fluentui/react-components';
import { CheckmarkSquare20Filled, Square20Regular, Edit16Regular, Delete16Regular } from '@fluentui/react-icons';
import { toast } from 'react-toastify';
import axiosInstance from '../../apiServices/axios';
import { updateWorkspaceDefaults } from '../../redux/usersSlice';
import wsStyles from '../WorkspaceSettings/css/index.module.css';
import styles from './WorkspaceSetupWizard.module.css';
import { resolveWorkspaceLensFeatures } from '../../utils/lensFeatures';
import ProjectTypesEditor from '../CommonComponents/ProjectTypesEditor/ProjectTypesEditor';
import { DEFAULT_WORKSPACE_PROJECT_TYPES, normalizeProjectTypeOptions, toProjectTypeOptions } from '../../utils/projectTypes';
import {
    IDEAL_CUSTOMER_PROFILE_PLACEHOLDER,
    OFFERINGS_PLACEHOLDER,
    ORG_DESCRIPTION_PLACEHOLDER,
    WORKSPACE_ORG_TYPES,
} from '../../utils/workspaceOrgProfile';
import {
    EMPTY_COMPANY_VALUE,
    EMPTY_PULSE_PROFILE,
    editingPulseFlagsFromSaved,
    normalizeCompanyValues,
    normalizePulseProfile,
    pulseProfileFromApi,
} from '../../utils/workspacePulseProfile';
import WorkspaceSetupStep6ValuesPulse from './WorkspaceSetupStep6ValuesPulse';

const REQUIRED_FEATURES = ['project_overview', 'dataroom', 'information_requests', 'project_management'];
const RECOMMENDED_FEATURES = ['journal', 'lens_intelligence'];

const EMPTY_ORG_PROFILE = {
    org_type: '',
    org_description: '',
    offerings: '',
    ideal_customer_profile: '',
};

const normalizeOrgProfile = profile => ({
    org_type: (profile?.org_type || '').trim(),
    org_description: (profile?.org_description || '').trim(),
    offerings: (profile?.offerings || '').trim(),
    ideal_customer_profile: (profile?.ideal_customer_profile || '').trim(),
});

const orgProfileFromApi = data =>
    normalizeOrgProfile({
        org_type: data?.org_type,
        org_description: data?.org_description,
        offerings: data?.offerings,
        ideal_customer_profile: data?.ideal_customer_profile,
    });

const editingOrgFlagsFromSaved = saved => ({
    org_type: !saved.org_type,
    org_description: !saved.org_description,
    offerings: !saved.offerings,
    ideal_customer_profile: !saved.ideal_customer_profile,
});
const DEFAULT_PROJECT_STATUSES = [
    { name: 'Pipeline', color: '#F8047E' },
    { name: 'Pre-Deal', color: '#FF0D0D' },
    { name: 'Due Diligence', color: '#FF8E15' },
    { name: 'On Hold', color: '#FAB733' },
    { name: 'Closed - Won', color: '#ACB334' },
    { name: 'Closed - Lost', color: '#C6C8D2' },
];
const FEATURE_CATEGORIES = {
    strategic_assessment: {
        name: 'Strategic Assessment & Monitoring',
        features: {
            project_overview: {
                name: 'Project Overview',
                description: 'Every project starts with an overview dashboard summarising deal progress, key metrics, and team activity.',
            },
            maturity_index: {
                name: 'Maturity Index',
                description: 'Assess the technology maturity of a target company using a structured scoring framework across key capability areas.',
            },
            security_index: {
                name: 'Security Index',
                description: "Evaluate the target company's security posture across infrastructure, policies, and practices.",
            },
            team_analyser: {
                name: 'Team Analyser',
                description: "Review and document the organisational structure, leadership capability, and cultural fit of the target company's team.",
            },
            security_governance: {
                name: 'Security Governance',
                description: 'Assess the governance frameworks, compliance posture, and security policies in place at the target company.',
            },
        },
    },
    due_diligence: {
        name: 'Due Diligence & Knowledge',
        features: {
            dataroom: {
                name: 'Dataroom',
                description: 'A secure, multi-workstream virtual data room for managing and sharing deal documents with granular access controls.',
            },
            information_requests: {
                name: 'Information Requests',
                description: 'Manage information and document requests across workstreams and parties. Track responses, follow-ups, and outstanding items in one place.',
            },
            dataroom_wizard: {
                name: 'Dataroom Wizard',
                description: 'A guided tool to help structure and prepare a data room.',
            },
            observations: {
                name: 'Observations',
                description: 'Capture, categorise, and manage consultant and team findings during due diligence. Linked to workstreams and deal stages.',
            },
            lessons_learnt: {
                name: 'Lessons Learnt',
                description: 'Record and access institutional knowledge from completed deals. Build a compounding knowledge base across your portfolio.',
            },
            journal: {
                name: 'Journal',
                description: 'A shared note-taking space for deal teams. Capture informal insights, meeting notes, and commentary throughout the deal lifecycle.',
            },
            lens_intelligence: {
                name: 'Lens Intelligence',
                description: 'Ask AI across documents, IRL responses, and observations with evidence-verified answers and saved chats.',
            },
        },
    },
    project_risk_management: {
        name: 'Project & Risk Management',
        features: {
            project_management: {
                name: 'Project Management',
                description: 'Full project and task management with Kanban boards, task assignment, priorities, due dates, and progress tracking.',
            },
            raid_log: {
                name: 'RAID Log',
                description: 'Track Risks, Assumptions, Issues, and Dependencies across the deal. Flag, assign, and monitor resolution in real time.',
            },
        },
    },
    technical_service: {
        name: 'Technical & Service Delivery',
        features: {
            software_inventory: {
                name: 'Software Inventory',
                description: "Document and assess the target company's software stack, licensing arrangements, and technology dependencies.",
            },
        },
    },
};

const WorkspaceSetupWizard = () => {
    const { workspaceId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);

    const membership = useSelector(state => (Array.isArray(state?.user?.memberships) ? state.user.memberships.find(m => String(m?.workspace?.id) === String(workspaceId)) : null));

    const [workspaceName, setWorkspaceName] = useState(() => membership?.workspace?.name || '');
    const [logoFile, setLogoFile] = useState(null);
    const [logoObjectUrl, setLogoObjectUrl] = useState(null);
    const [serverLogoUrl, setServerLogoUrl] = useState(null);
    const [selectedFeatures, setSelectedFeatures] = useState([]);
    const [projectTypes, setProjectTypes] = useState([]);
    const [projectStatuses, setProjectStatuses] = useState([]);
    const [newProjectStatus, setNewProjectStatus] = useState('');
    const [newProjectStatusColor, setNewProjectStatusColor] = useState('#5CC8FF');
    const [editingStatusIndex, setEditingStatusIndex] = useState(null);
    const [editingStatusName, setEditingStatusName] = useState('');
    const [editingStatusColor, setEditingStatusColor] = useState('#5CC8FF');
    const [draggedStatusIndex, setDraggedStatusIndex] = useState(null);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteUserType, setInviteUserType] = useState('');
    const [inviteSubmitting, setInviteSubmitting] = useState(false);
    const [workspaceMembers, setWorkspaceMembers] = useState([]);
    const [workspaceInvitations, setWorkspaceInvitations] = useState([]);
    const [revokingInvitationId, setRevokingInvitationId] = useState(null);
    const [savedOrgProfile, setSavedOrgProfile] = useState(EMPTY_ORG_PROFILE);
    const [draftOrgProfile, setDraftOrgProfile] = useState(EMPTY_ORG_PROFILE);
    const [editingOrgProfile, setEditingOrgProfile] = useState({
        org_type: true,
        org_description: true,
        offerings: true,
        ideal_customer_profile: true,
    });
    const [orgProfileSaving, setOrgProfileSaving] = useState(false);
    const [savedPulseProfile, setSavedPulseProfile] = useState(EMPTY_PULSE_PROFILE);
    const [draftPulseProfile, setDraftPulseProfile] = useState(EMPTY_PULSE_PROFILE);
    const [editingPulseProfile, setEditingPulseProfile] = useState({
        coaching_style: true,
        pulse_frequency: true,
        intelligence_sensitivity: true,
        pulse_bias: true,
    });
    const [valueDraft, setValueDraft] = useState(EMPTY_COMPANY_VALUE);
    const [editingValueIndex, setEditingValueIndex] = useState(null);
    const [pulseProfileSaving, setPulseProfileSaving] = useState(false);
    const [valueSaving, setValueSaving] = useState(false);
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setError('');
            try {
                const { data } = await axiosInstance.get(`accounts/workspaces/${workspaceId}/admin/settings/`);
                if (cancelled) return;
                if (typeof data?.name === 'string') {
                    setWorkspaceName(data.name);
                }
                setServerLogoUrl(data.workspace_logo || null);
                const defaults = Array.isArray(data?.default_lens_features) ? data.default_lens_features : [];
                setSelectedFeatures(resolveWorkspaceLensFeatures(defaults, REQUIRED_FEATURES));
                const typeOptionsRaw = Array.isArray(data?.project_type_options) ? data.project_type_options : [];
                const statusOptionsRaw = Array.isArray(data?.project_status_options) ? data.project_status_options : [];
                const normalizedTypes = normalizeProjectTypeOptions(
                    typeOptionsRaw.length ? typeOptionsRaw : toProjectTypeOptions(DEFAULT_WORKSPACE_PROJECT_TYPES),
                );
                const normalizedStatuses = statusOptionsRaw
                    .map(item => ({
                        name: typeof item?.name === 'string' ? item.name.trim() : '',
                        color: typeof item?.color === 'string' && item.color ? item.color : '#5CC8FF',
                    }))
                    .filter(item => item.name);
                setProjectTypes(normalizedTypes);
                setProjectStatuses(normalizedStatuses.length ? normalizedStatuses : DEFAULT_PROJECT_STATUSES);
                const loadedOrgProfile = orgProfileFromApi(data);
                setSavedOrgProfile(loadedOrgProfile);
                setDraftOrgProfile(loadedOrgProfile);
                setEditingOrgProfile(editingOrgFlagsFromSaved(loadedOrgProfile));
                const loadedPulseProfile = pulseProfileFromApi(data);
                setSavedPulseProfile(loadedPulseProfile);
                setDraftPulseProfile(loadedPulseProfile);
                setEditingPulseProfile(editingPulseFlagsFromSaved(loadedPulseProfile));
            } catch (e) {
                if (!cancelled) {
                    setError('Could not load workspace. Please refresh and try again.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [workspaceId]);

    const refreshWorkspaceMembers = useCallback(async () => {
        if (!workspaceId) return;
        try {
            const { data } = await axiosInstance.get(`accounts/workspaces/${workspaceId}/admin/members/`);
            const members = Array.isArray(data?.members) ? data.members : [];
            const invites = Array.isArray(data?.invitations) ? data.invitations : [];
            setWorkspaceMembers(members);
            setWorkspaceInvitations(invites);
        } catch {
            // Silent failure – Step 4 can still be used without the list.
        }
    }, [workspaceId]);

    useEffect(() => {
        refreshWorkspaceMembers();
    }, [refreshWorkspaceMembers]);

    useEffect(() => {
        return () => {
            if (logoObjectUrl) {
                URL.revokeObjectURL(logoObjectUrl);
            }
        };
    }, [logoObjectUrl]);

    const displayLogoUrl = logoObjectUrl || serverLogoUrl;

    const hasLogo = Boolean(logoFile || serverLogoUrl);
    const nameOk = (workspaceName || '').trim().length > 0;
    const canContinueStep1 = nameOk && hasLogo && !loading;
    const workspaceNameLabel = (workspaceName || membership?.workspace?.name || 'Workspace').trim() || 'Workspace';

    const hasOrgProfileChanges = useMemo(() => {
        const saved = normalizeOrgProfile(savedOrgProfile);
        const draft = normalizeOrgProfile(draftOrgProfile);
        return JSON.stringify(saved) !== JSON.stringify(draft);
    }, [savedOrgProfile, draftOrgProfile]);

    const hasValueDraftChanges = useMemo(() => {
        const draft = {
            name: (valueDraft?.name || '').trim(),
            description: (valueDraft?.description || '').trim(),
            expected_behavior: (valueDraft?.expected_behavior || '').trim(),
        };
        return Boolean(draft.name || draft.description || draft.expected_behavior);
    }, [valueDraft]);

    const hasPulsePreferenceChanges = useMemo(() => {
        const saved = normalizePulseProfile(savedPulseProfile);
        const draft = normalizePulseProfile(draftPulseProfile);
        return JSON.stringify(saved) !== JSON.stringify(draft);
    }, [savedPulseProfile, draftPulseProfile]);

    const hasPulseChanges = hasValueDraftChanges || hasPulsePreferenceChanges;

    const savedCompanyValues = useMemo(() => normalizeCompanyValues(savedPulseProfile.company_values), [savedPulseProfile.company_values]);

    const updateDraftOrgField = (key, value) => {
        setDraftOrgProfile(prev => ({ ...prev, [key]: value }));
    };

    const startEditOrgField = key => {
        setEditingOrgProfile(prev => ({ ...prev, [key]: true }));
    };

    const saveOrgProfile = async () => {
        if (orgProfileSaving) return;
        setError('');
        setOrgProfileSaving(true);
        try {
            const payload = normalizeOrgProfile(draftOrgProfile);
            const { data } = await axiosInstance.patch(`accounts/workspaces/${workspaceId}/admin/settings/`, payload);
            const saved = orgProfileFromApi(data);
            setSavedOrgProfile(saved);
            setDraftOrgProfile(saved);
            setEditingOrgProfile(editingOrgFlagsFromSaved(saved));
            dispatch(
                updateWorkspaceDefaults({
                    workspaceId,
                    patch: saved,
                }),
            );
            toast.success('Organisation details saved', { position: 'top-right' });
            setStep(6);
        } catch (e) {
            const msg = e?.response?.data?.org_type?.[0] || e?.response?.data?.detail || 'Could not save. Please try again.';
            setError(typeof msg === 'string' ? msg : 'Could not save. Please try again.');
        } finally {
            setOrgProfileSaving(false);
        }
    };

    const handleOrgProfileCancel = () => {
        const saved = normalizeOrgProfile(savedOrgProfile);
        setDraftOrgProfile(saved);
        setEditingOrgProfile(editingOrgFlagsFromSaved(saved));
        setError('');
    };

    const updateDraftPulseField = (key, value) => {
        setDraftPulseProfile(prev => ({ ...prev, [key]: value }));
    };

    const startEditPulseField = key => {
        setEditingPulseProfile(prev => ({ ...prev, [key]: true }));
    };

    const updateValueDraftField = (key, value) => {
        setValueDraft(prev => ({ ...prev, [key]: value }));
    };

    const handleEditSavedValue = index => {
        const value = savedCompanyValues[index];
        if (!value) return;
        setEditingValueIndex(index);
        setValueDraft({
            name: value.name || '',
            description: value.description || '',
            expected_behavior: value.expected_behavior || '',
        });
    };

    const handleDeleteSavedValue = async index => {
        if (valueSaving) return;
        const nextValues = savedCompanyValues.filter((_, i) => i !== index);
        setError('');
        setValueSaving(true);
        try {
            const { data } = await axiosInstance.patch(`accounts/workspaces/${workspaceId}/admin/settings/`, {
                company_values: nextValues,
            });
            const saved = pulseProfileFromApi(data);
            setSavedPulseProfile(saved);
            setDraftPulseProfile(prev => ({ ...prev, company_values: saved.company_values }));
            if (editingValueIndex === index) {
                setValueDraft(EMPTY_COMPANY_VALUE);
                setEditingValueIndex(null);
            } else if (editingValueIndex !== null && editingValueIndex > index) {
                setEditingValueIndex(editingValueIndex - 1);
            }
            dispatch(
                updateWorkspaceDefaults({
                    workspaceId,
                    patch: {
                        company_values: saved.company_values,
                    },
                }),
            );
            toast.success('Company value removed', { position: 'top-right' });
        } catch (e) {
            const msg = e?.response?.data?.company_values?.[0] || e?.response?.data?.detail || 'Could not delete. Please try again.';
            setError(typeof msg === 'string' ? msg : 'Could not delete. Please try again.');
        } finally {
            setValueSaving(false);
        }
    };

    const saveValueAndAddAnother = async () => {
        const name = (valueDraft?.name || '').trim();
        if (!name || valueSaving) return;
        setError('');
        setValueSaving(true);
        try {
            const entry = {
                name,
                description: (valueDraft?.description || '').trim(),
                expected_behavior: (valueDraft?.expected_behavior || '').trim(),
            };
            const nextValues = [...savedCompanyValues];
            if (editingValueIndex !== null) {
                nextValues[editingValueIndex] = entry;
            } else {
                nextValues.push(entry);
            }
            const { data } = await axiosInstance.patch(`accounts/workspaces/${workspaceId}/admin/settings/`, {
                company_values: nextValues,
            });
            const saved = pulseProfileFromApi(data);
            setSavedPulseProfile(saved);
            setDraftPulseProfile(prev => ({ ...prev, company_values: saved.company_values }));
            setValueDraft(EMPTY_COMPANY_VALUE);
            setEditingValueIndex(null);
            dispatch(
                updateWorkspaceDefaults({
                    workspaceId,
                    patch: {
                        company_values: saved.company_values,
                    },
                }),
            );
            toast.success(editingValueIndex !== null ? 'Company value updated' : 'Company value saved', { position: 'top-right' });
        } catch (e) {
            const msg = e?.response?.data?.company_values?.[0] || e?.response?.data?.detail || 'Could not save. Please try again.';
            setError(typeof msg === 'string' ? msg : 'Could not save. Please try again.');
        } finally {
            setValueSaving(false);
        }
    };

    const savePulseProfile = async () => {
        if (pulseProfileSaving) return;
        setError('');
        setPulseProfileSaving(true);
        try {
            let nextValues = [...savedCompanyValues];
            const name = (valueDraft?.name || '').trim();
            if (name) {
                const entry = {
                    name,
                    description: (valueDraft?.description || '').trim(),
                    expected_behavior: (valueDraft?.expected_behavior || '').trim(),
                };
                if (editingValueIndex !== null) {
                    nextValues[editingValueIndex] = entry;
                } else {
                    nextValues.push(entry);
                }
            }
            const payload = {
                ...normalizePulseProfile(draftPulseProfile),
                company_values: nextValues,
            };
            const { data } = await axiosInstance.patch(`accounts/workspaces/${workspaceId}/admin/settings/`, payload);
            const saved = pulseProfileFromApi(data);
            setSavedPulseProfile(saved);
            setDraftPulseProfile(saved);
            setEditingPulseProfile(editingPulseFlagsFromSaved(saved));
            setValueDraft(EMPTY_COMPANY_VALUE);
            setEditingValueIndex(null);
            dispatch(
                updateWorkspaceDefaults({
                    workspaceId,
                    patch: saved,
                }),
            );
            toast.success('Lens Pulse preferences saved', { position: 'top-right' });
        } catch (e) {
            const msg =
                e?.response?.data?.coaching_style?.[0] ||
                e?.response?.data?.pulse_frequency?.[0] ||
                e?.response?.data?.intelligence_sensitivity?.[0] ||
                e?.response?.data?.pulse_bias?.[0] ||
                e?.response?.data?.detail ||
                'Could not save. Please try again.';
            setError(typeof msg === 'string' ? msg : 'Could not save. Please try again.');
        } finally {
            setPulseProfileSaving(false);
        }
    };

    const handlePulseProfileCancel = () => {
        const saved = normalizePulseProfile(savedPulseProfile);
        setDraftPulseProfile(saved);
        setEditingPulseProfile(editingPulseFlagsFromSaved(saved));
        setValueDraft(EMPTY_COMPANY_VALUE);
        setEditingValueIndex(null);
        setError('');
    };

    const allFeatureKeys = useMemo(() => Object.values(FEATURE_CATEGORIES).flatMap(category => Object.keys(category.features)), []);

    const openFilePicker = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const onFileChange = e => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        const okType = /^image\/(png|jpeg|jpg)$/i.test(f.type);
        if (!okType) {
            setError('Please use a PNG or JPG image.');
            return;
        }
        setError('');
        setLogoFile(f);
        setLogoObjectUrl(prev => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(f);
        });
    };

    const toggleFeature = featureKey => {
        if (REQUIRED_FEATURES.includes(featureKey)) return;
        setSelectedFeatures(prev => {
            const has = prev.includes(featureKey);
            const next = has ? prev.filter(k => k !== featureKey) : [...prev, featureKey];
            return Array.from(new Set([...REQUIRED_FEATURES, ...next]));
        });
    };

    const handleAddAllFeatures = () => {
        setSelectedFeatures(Array.from(new Set([...REQUIRED_FEATURES, ...allFeatureKeys])));
    };

    const handleRemoveAllOptionalFeatures = () => {
        setSelectedFeatures([...REQUIRED_FEATURES]);
    };

    const saveStep1 = async () => {
        if (!canContinueStep1 || submitting) return;
        setError('');
        setSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('name', workspaceName.trim());
            if (logoFile) {
                formData.append('workspace_logo', logoFile);
            }
            const { data } = await axiosInstance.patch(`accounts/workspaces/${workspaceId}/admin/settings/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            dispatch(
                updateWorkspaceDefaults({
                    workspaceId,
                    patch: {
                        name: data.name,
                        workspace_logo: data.workspace_logo,
                    },
                }),
            );
        } catch (e) {
            const msg = e?.response?.data?.name?.[0] || e?.response?.data?.workspace_logo?.[0] || e?.response?.data?.detail || 'Could not save. Please try again.';
            setError(typeof msg === 'string' ? msg : 'Could not save. Please try again.');
            throw e;
        } finally {
            setSubmitting(false);
        }
    };

    const saveStep2 = async () => {
        if (submitting) return;
        setError('');
        setSubmitting(true);
        try {
            const { data } = await axiosInstance.patch(`accounts/workspaces/${workspaceId}/admin/settings/`, {
                default_lens_features: selectedFeatures,
            });
            dispatch(
                updateWorkspaceDefaults({
                    workspaceId,
                    patch: {
                        default_lens_features: data.default_lens_features,
                    },
                }),
            );
        } catch (e) {
            const msg = e?.response?.data?.detail || 'Could not save. Please try again.';
            setError(typeof msg === 'string' ? msg : 'Could not save. Please try again.');
            throw e;
        } finally {
            setSubmitting(false);
        }
    };

    const saveStep3 = async () => {
        if (submitting) return;
        setError('');
        setSubmitting(true);
        try {
            const payload = {
                project_type_options: normalizeProjectTypeOptions(projectTypes),
                project_status_options: projectStatuses.map((item, idx) => ({ name: item.name, color: item.color, order: idx })),
            };
            const { data } = await axiosInstance.patch(`accounts/workspaces/${workspaceId}/admin/settings/`, payload);
            dispatch(
                updateWorkspaceDefaults({
                    workspaceId,
                    patch: {
                        project_type_options: data.project_type_options,
                        project_status_options: data.project_status_options,
                    },
                }),
            );
        } catch (e) {
            const msg = e?.response?.data?.detail || 'Could not save. Please try again.';
            setError(typeof msg === 'string' ? msg : 'Could not save. Please try again.');
            throw e;
        } finally {
            setSubmitting(false);
        }
    };

    const goNext = async () => {
        if (step === 1 && !canContinueStep1) return;
        setError('');
        try {
            if (step === 1) {
                await saveStep1();
            } else if (step === 2) {
                await saveStep2();
            } else if (step === 3) {
                await saveStep3();
            }
            setStep(prev => Math.min(prev + 1, 6));
        } catch {
            // error already surfaced via setError
        }
    };

    const goToStep6 = () => {
        if (hasOrgProfileChanges) return;
        setError('');
        setStep(6);
    };

    const finishSetup = async () => {
        if (submitting) return;
        setError('');
        setSubmitting(true);
        try {
            const { data } = await axiosInstance.patch(`accounts/workspaces/${workspaceId}/admin/settings/`, {
                is_setuped: true,
            });
            dispatch(
                updateWorkspaceDefaults({
                    workspaceId,
                    patch: {
                        is_setuped: data.is_setuped,
                    },
                }),
            );
            navigate('/project', { replace: true });
        } catch (e) {
            const msg = e?.response?.data?.detail || 'Could not finish setup. Please try again.';
            setError(typeof msg === 'string' ? msg : 'Could not finish setup. Please try again.');
            throw e;
        } finally {
            setSubmitting(false);
        }
    };

    const goBack = () => {
        setError('');
        setStep(prev => Math.max(prev - 1, 1));
    };

    const addProjectStatus = () => {
        const value = (newProjectStatus || '').trim();
        if (!value) return;
        if (projectStatuses.some(item => item.name.toLowerCase() === value.toLowerCase())) return;
        setProjectStatuses(prev => [...prev, { name: value, color: newProjectStatusColor || '#5CC8FF' }]);
        setNewProjectStatus('');
    };

    const removeProjectStatus = index => {
        setProjectStatuses(prev => prev.filter((_, i) => i !== index));
    };

    const startEditProjectStatus = index => {
        const status = projectStatuses[index];
        if (!status) return;
        setEditingStatusIndex(index);
        setEditingStatusName(status.name);
        setEditingStatusColor(status.color || '#5CC8FF');
    };

    const saveEditProjectStatus = () => {
        const value = (editingStatusName || '').trim();
        if (editingStatusIndex == null || !value) {
            setEditingStatusIndex(null);
            return;
        }
        setProjectStatuses(prev =>
            prev.map((item, i) =>
                i === editingStatusIndex
                    ? {
                          ...item,
                          name: value,
                          color: editingStatusColor || '#5CC8FF',
                      }
                    : item,
            ),
        );
        setEditingStatusIndex(null);
    };

    const handleStatusDragStart = index => {
        setDraggedStatusIndex(index);
    };

    const handleStatusDragOver = event => {
        event.preventDefault();
    };

    const handleStatusDrop = index => {
        if (draggedStatusIndex === null || draggedStatusIndex === index) return;
        const updated = [...projectStatuses];
        const [moved] = updated.splice(draggedStatusIndex, 1);
        updated.splice(index, 0, moved);
        setDraggedStatusIndex(null);
        setProjectStatuses(updated);
    };

    const isEmail = str => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(str);
    };

    const handleSendInvite = async () => {
        const email = (inviteEmail || '').trim();
        if (!email || !inviteUserType || inviteSubmitting) return;
        if (!isEmail(email)) {
            const msg = 'Please enter a valid email address.';
            setError(msg);
            toast.error(msg, { position: 'top-right' });
            return;
        }
        setError('');
        setInviteSubmitting(true);
        try {
            const role = inviteUserType === 'FULL_ACCESS' ? 'WORKSPACE_MEMBER' : 'WORKSPACE_MEMBER';
            const { data } = await axiosInstance.post(`accounts/workspaces/${workspaceId}/invite/`, {
                email,
                role,
            });
            setInviteEmail('');
            setInviteUserType('');
            await refreshWorkspaceMembers();
            const detail = data?.detail || 'Invite sent';
            toast.success(detail, { position: 'top-right' });
        } catch (e) {
            const msg = e?.response?.data?.email || e?.response?.data?.detail || e?.response?.data?.message || 'Could not send invite. Please try again.';
            const finalMsg = typeof msg === 'string' ? msg : 'Could not send invite. Please try again.';
            setError(finalMsg);
            toast.error(finalMsg, { position: 'top-right' });
        } finally {
            setInviteSubmitting(false);
        }
    };

    const handleFinalSubmit = async () => {
        if (step !== 6 || submitting || hasPulseChanges) return;
        try {
            await finishSetup();
        } catch {
            // error already surfaced via setError
        }
    };

    const renderStep1 = () => (
        <div style={{ maxWidth: '400px' }}>
            <p className={styles.step}>Step 1 of 6</p>
            <h1 className={styles.mainTitle}>Set up your workspace</h1>
            <div className={styles.fieldBlock}>
                <div className={styles.labelRow}>
                    <label className={styles.fieldLabel} htmlFor="workspace-setup-name">
                        Workspace Name
                    </label>
                </div>
                <div className={styles.nameInput}>
                    <Input
                        id="workspace-setup-name"
                        value={workspaceName}
                        onChange={(e, data) => setWorkspaceName(data.value)}
                        placeholder="Pre-populated, editable text input"
                        appearance="outline"
                    />
                </div>
            </div>
            <div className={styles.fieldBlock}>
                <div className="d-flex align-items-center justify-content-between">
                    <span className={styles.fieldLabel}>Workspace Logo</span>
                    <button type="button" className={styles.changeLink} onClick={openFilePicker}>
                        Change
                    </button>
                </div>

                <input ref={fileInputRef} className={styles.hiddenInput} type="file" accept="image/png,image/jpeg,image/jpg" aria-hidden tabIndex={-1} onChange={onFileChange} />

                <div
                    className={styles.dropzone}
                    role="button"
                    tabIndex={0}
                    onClick={openFilePicker}
                    onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openFilePicker();
                        }
                    }}>
                    {displayLogoUrl ? (
                        <div className={styles.previewWrap}>
                            <img className={styles.previewImg} src={displayLogoUrl} alt="" />
                        </div>
                    ) : null}
                    {!displayLogoUrl && <p className={styles.dropzoneHint}>{'Supported file formats: PNG/JPG\nRecommended size 200×200px\n\nYou can add or change this later'}</p>}
                </div>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div style={{ maxWidth: '550px' }}>
            <p className={styles.step}>{`Step 2 of 6  |  ${workspaceNameLabel}`}</p>
            <h1 className={styles.mainTitle}>Choose what your team sees by default</h1>
            <p className={styles.stepDescription}>
                Select the features enabled across all projects in your workspace. Required features are always on. All others can be turned on or off - Project Admins can adjust
                these per project at any time.
            </p>
            <p className={styles.stepDescriptionBold}>Feature selection applies to Full Access users only. VDR users have access to Dataroom and Information Requests only.</p>

            {Object.entries(FEATURE_CATEGORIES).map(([key, category]) => (
                <div key={key} className={styles.featureCategory}>
                    <h3 className={styles.categoryTitle}>{category.name}</h3>
                    {Object.entries(category.features).map(([featureKey, feature]) => {
                        const checked = selectedFeatures.includes(featureKey);
                        const required = REQUIRED_FEATURES.includes(featureKey);
                        const recommended = RECOMMENDED_FEATURES.includes(featureKey);
                        return (
                            <label key={featureKey} className={styles.featureItem}>
                                <span className={styles.featureIconWrap}>
                                    {checked ? (
                                        <CheckmarkSquare20Filled className={required ? styles.requiredIcon : styles.checkedIcon} />
                                    ) : (
                                        <Square20Regular className={styles.uncheckedIcon} />
                                    )}
                                </span>
                                <input type="checkbox" className={styles.featureNativeInput} checked={checked} disabled={required} onChange={() => toggleFeature(featureKey)} />
                                <span className={styles.featureTextWrap}>
                                    <span className={styles.featureTitle}>
                                        {feature.name}
                                        {required ? <span className={styles.featureMeta}> (Required)</span> : null}
                                        {!required && recommended ? <span className={styles.featureMeta}> (Recommended)</span> : null}
                                    </span>
                                    <span className={styles.featureDescription}>{feature.description}</span>
                                </span>
                            </label>
                        );
                    })}
                </div>
            ))}
        </div>
    );

    const renderStep3 = () => (
        <div style={{ maxWidth: '500px' }}>
            <p className={styles.step}>{`Step 3 of 6  |  ${workspaceNameLabel}`}</p>
            <h1 className={styles.mainTitle}>Define your project settings</h1>
            <p className={styles.stepDescription}>
                Review and customise the default project types and lifecycle stages for your workspace. These are pre-configured based on your setup - edit, reorder, or add your
                own.
            </p>

            <div className={styles.step3Section}>
                <h3 className={styles.categoryTitle}>Default Project Types</h3>
                <p className={styles.step3Hint}>Project types help categorise deals across your portfolio. Project Admins select a type when creating a new project.</p>
                <ProjectTypesEditor projectTypes={projectTypes} onChange={setProjectTypes} />
            </div>

            <div className={styles.step3Section}>
                <h3 className={styles.categoryTitle}>Default Project Statuses</h3>
                <p className={styles.step3Hint}>
                    These stages define your deal lifecycle and appear as columns on your portfolio Kanban board. Drag to reorder.
                    <span className={styles.step3HintItalic}> Note: The order shown here reflects the left-to-right column order on your portfolio Kanban board.</span>
                </p>
                {projectStatuses.length === 0 ? (
                    <div className={wsStyles.centered}>No default project statuses yet.</div>
                ) : (
                    <ul className={wsStyles.statusList}>
                        {projectStatuses.map((statusItem, idx) => (
                            <li
                                key={`${statusItem.name}-${idx}`}
                                className={draggedStatusIndex === idx ? `${wsStyles.statusRow} ${wsStyles.statusRowDragging}` : wsStyles.statusRow}
                                draggable
                                onDragStart={() => handleStatusDragStart(idx)}
                                onDragOver={handleStatusDragOver}
                                onDrop={() => handleStatusDrop(idx)}>
                                <div className={wsStyles.statusMain}>
                                    {editingStatusIndex !== idx && <span className={wsStyles.statusDragHandle}>⋮⋮</span>}
                                    {editingStatusIndex === idx ? (
                                        <>
                                            <input
                                                type="text"
                                                className={wsStyles.statusEditInput}
                                                value={editingStatusName}
                                                onChange={e => setEditingStatusName(e.target.value)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        saveEditProjectStatus();
                                                    } else if (e.key === 'Escape') {
                                                        e.preventDefault();
                                                        setEditingStatusIndex(null);
                                                    }
                                                }}
                                            />
                                            <div className={wsStyles.statusColorInputWrapper}>
                                                <input
                                                    type="color"
                                                    className={wsStyles.statusColorInput}
                                                    value={editingStatusColor}
                                                    onChange={e => setEditingStatusColor(e.target.value)}
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <span className={wsStyles.statusName}>{statusItem.name}</span>
                                    )}
                                </div>
                                <div className={wsStyles.statusActions}>
                                    {editingStatusIndex !== idx && <span className={wsStyles.statusColorDot} style={{ backgroundColor: statusItem.color || '#c89741' }} />}
                                    {editingStatusIndex === idx ? (
                                        <>
                                            <button type="button" className={wsStyles.saveTypeButton} onClick={saveEditProjectStatus}>
                                                Save
                                            </button>
                                            <button type="button" className={wsStyles.deleteTypeButton} onClick={() => setEditingStatusIndex(null)}>
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <Edit16Regular className="ms-3" color="var(--Neutral-Gray-6)" type="button" onClick={() => startEditProjectStatus(idx)} />
                                            <Delete16Regular className="ms-3" color="var(--Neutral-Gray-6)" type="button" onClick={() => removeProjectStatus(idx)} />
                                        </>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
                <div className={wsStyles.statusInputRow}>
                    <input
                        type="text"
                        className={wsStyles.projectTypeInput}
                        placeholder="Add New Project Status"
                        value={newProjectStatus}
                        onChange={e => setNewProjectStatus(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                addProjectStatus();
                            }
                        }}
                    />
                    <div className={wsStyles.statusColorInputWrapper}>
                        <input type="color" className={wsStyles.statusColorInput} value={newProjectStatusColor} onChange={e => setNewProjectStatusColor(e.target.value)} />
                    </div>
                    <button type="button" className={wsStyles.addTypeButton} onClick={addProjectStatus} disabled={!newProjectStatus.trim()}>
                        Add
                    </button>
                </div>
            </div>
        </div>
    );

    const renderStep4 = () => (
        <div style={{ maxWidth: '720px' }}>
            <p className={styles.step}>{`Step 4 of 6  |  ${workspaceNameLabel}`}</p>
            <h1 className={styles.mainTitle}>
                Invite your team to Lens <span className={styles.stepMeta}>(Optional)</span>
            </h1>
            <p className={styles.stepDescription}>
                Add your team members to the workspace. Workspace members can also be invited by Project Admins when setting up a project. Each Full Access user is billed as a seat
                from their join date. You can manage users at any time from your workspace settings.
            </p>

            <div className={styles.step3Section}>
                <div className={styles.summaryRow}>
                    <span className={styles.summaryLabel}>Total seats: {workspaceMembers.length + workspaceInvitations.length}</span>
                </div>
                <p className={styles.step3Hint}>Billing starts from each user's join date.</p>
            </div>

            <div className={styles.step4Table}>
                <div className={styles.step4HeaderRow}>
                    <span className={styles.step4HeaderCell}>Email address</span>
                    <span className={styles.step4HeaderCell}>User Type</span>
                    <span className={styles.step4HeaderCell}>Join Date</span>
                    <span className={styles.step4HeaderCell}></span>
                </div>
                {workspaceMembers.map(member => {
                    const created = member.created_at ? new Date(member.created_at) : null;
                    const joinDate = created && !Number.isNaN(created.getTime()) ? created.toLocaleDateString() : '—';
                    return (
                        <div key={`member-${member.id}`} className={styles.step4BodyRow}>
                            <span className={styles.step4Cell}>{member.email}</span>
                            <span className={styles.step4Cell}>
                                {member.role === 'WORKSPACE_SUPER_ADMIN' ? 'Super Admin' : member.role === 'WORKSPACE_ADMIN' ? 'Workspace Admin' : 'Full Access User'}
                            </span>
                            <span className={styles.step4Cell}>{joinDate}</span>
                        </div>
                    );
                })}
                {workspaceInvitations.map(invite => {
                    const created = invite.created_date ? new Date(invite.created_date) : null;
                    const inviteDate = created && !Number.isNaN(created.getTime()) ? created.toLocaleDateString() : '';
                    const statusLabel = invite.is_expired ? 'Invitation expired' : 'Invited';
                    const actionLabel = invite.is_expired ? 'Resend Invitation' : 'Revoke Invitation';
                    const isBusy = revokingInvitationId === invite.id;

                    const handleInviteAction = async () => {
                        if (!invite.id || !workspaceId || isBusy) return;
                        // For now both expired and active use revoke on backend; resend can be added later.
                        setRevokingInvitationId(invite.id);
                        try {
                            await axiosInstance.delete(`accounts/workspaces/${workspaceId}/admin/invitations/${invite.id}/`);
                            await refreshWorkspaceMembers();
                            toast.success(invite.is_expired ? 'Invitation removed' : 'Invitation revoked', { position: 'top-right' });
                        } catch (e) {
                            const msg = e?.response?.data?.detail || e?.response?.data?.message || 'Could not update invitation. Please try again.';
                            toast.error(typeof msg === 'string' ? msg : 'Could not update invitation. Please try again.', {
                                position: 'top-right',
                            });
                        } finally {
                            setRevokingInvitationId(null);
                        }
                    };

                    return (
                        <div key={`invite-${invite.id || invite.email}`} className={styles.step4BodyRow}>
                            <span className={styles.step4Cell}>{invite.email}</span>
                            <span className={styles.step4Cell}>
                                {invite.role === 'WORKSPACE_SUPER_ADMIN' ? 'Super Admin' : invite.role === 'WORKSPACE_ADMIN' ? 'Workspace Admin' : 'Full Access User'}
                            </span>
                            <span className={styles.step4Cell}>{statusLabel} </span>
                            <span className={styles.step4Cell}>
                                {' '}
                                <button type="button" className={styles.stepActionLink} disabled={isBusy} onClick={handleInviteAction}>
                                    {actionLabel}
                                </button>
                            </span>
                        </div>
                    );
                })}
            </div>

            <div className={styles.step4InviteRow}>
                <input className={styles.step4EmailInput} placeholder="Add email address" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} type="email" />
                <select className={styles.step4UserTypeBtn} value={inviteUserType} onChange={e => setInviteUserType(e.target.value)}>
                    <option value="" disabled>
                        Select User Type
                    </option>
                    <option value="FULL_ACCESS">Full Access User</option>
                    <option value="VDR">VDR User</option>
                </select>
                <button type="button" className={styles.step4SendBtn} disabled={!inviteEmail.trim() || !inviteUserType || inviteSubmitting} onClick={handleSendInvite}>
                    {inviteSubmitting ? 'Sending…' : 'Send Invite'}
                </button>
            </div>
        </div>
    );

    const renderStep5 = () => {
        const renderOrgSectionHeader = (label, fieldKey) => (
            <div className={styles.orgSectionHeader}>
                <p className={`${styles.fieldLabel} mb-0`}>{label}</p>
                {!editingOrgProfile[fieldKey] ? (
                    <button type="button" className={styles.changeLink} onClick={() => startEditOrgField(fieldKey)}>
                        Edit
                    </button>
                ) : null}
            </div>
        );

        return (
            <div style={{ maxWidth: '560px' }}>
                <p className={styles.step}>{`Step 5 of 6  |  ${workspaceNameLabel}`}</p>
                <h1 className={styles.mainTitle}>
                    Help Lens better understand your organisation <span className={styles.stepMeta}>(Optional)</span>
                </h1>
                <p className={styles.stepDescription} style={{ marginBottom: '24px' }}>
                    This information is optional and can be added or updated later. Providing it allows Lens Pulse to deliver more relevant coaching,
                    prioritisation and communication across your workspace.
                </p>

                <div className={styles.orgTextField}>
                    {renderOrgSectionHeader('Organisation Type', 'org_type')}
                    {editingOrgProfile.org_type ? (
                        <div className={styles.orgSelectWrap}>
                            <Select
                                className={styles.orgSelect}
                                value={draftOrgProfile.org_type}
                                onChange={e => updateDraftOrgField('org_type', e.target.value)}
                                name="org_type">
                                <option value="">Select</option>
                                {WORKSPACE_ORG_TYPES.map(type => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </Select>
                        </div>
                    ) : (
                        <p className={styles.orgSavedValue}>{savedOrgProfile.org_type || '—'}</p>
                    )}
                </div>

                <div className={styles.orgTextField}>
                    {renderOrgSectionHeader('Organisation Description', 'org_description')}
                    {editingOrgProfile.org_description ? (
                        <Textarea
                            className={styles.orgTextarea}
                            value={draftOrgProfile.org_description}
                            onChange={(e, data) => updateDraftOrgField('org_description', data.value)}
                            rows={6}
                            placeholder={ORG_DESCRIPTION_PLACEHOLDER}
                        />
                    ) : (
                        <p className={styles.orgSavedValue}>{savedOrgProfile.org_description || '—'}</p>
                    )}
                </div>

                <div className={styles.orgTextField}>
                    {renderOrgSectionHeader('Offerings', 'offerings')}
                    {editingOrgProfile.offerings ? (
                        <Textarea
                            className={styles.orgTextarea}
                            value={draftOrgProfile.offerings}
                            onChange={(e, data) => updateDraftOrgField('offerings', data.value)}
                            rows={6}
                            placeholder={OFFERINGS_PLACEHOLDER}
                        />
                    ) : (
                        <p className={styles.orgSavedValue}>{savedOrgProfile.offerings || '—'}</p>
                    )}
                </div>

                <div className={styles.orgTextField}>
                    {renderOrgSectionHeader('Ideal Customer Profile', 'ideal_customer_profile')}
                    {editingOrgProfile.ideal_customer_profile ? (
                        <Textarea
                            className={styles.orgTextarea}
                            value={draftOrgProfile.ideal_customer_profile}
                            onChange={(e, data) => updateDraftOrgField('ideal_customer_profile', data.value)}
                            rows={6}
                            placeholder={IDEAL_CUSTOMER_PROFILE_PLACEHOLDER}
                        />
                    ) : (
                        <p className={styles.orgSavedValue}>{savedOrgProfile.ideal_customer_profile || '—'}</p>
                    )}
                </div>
            </div>
        );
    };

    const renderStep6 = () => (
        <WorkspaceSetupStep6ValuesPulse
            workspaceNameLabel={workspaceNameLabel}
            savedCompanyValues={savedCompanyValues}
            valueDraft={valueDraft}
            onValueDraftChange={updateValueDraftField}
            editingValueIndex={editingValueIndex}
            onEditSavedValue={handleEditSavedValue}
            onDeleteSavedValue={handleDeleteSavedValue}
            onSaveValueAndAddAnother={saveValueAndAddAnother}
            valueSaving={valueSaving}
            draftPulseProfile={draftPulseProfile}
            savedPulseProfile={savedPulseProfile}
            editingPulseProfile={editingPulseProfile}
            onStartEditPulseField={startEditPulseField}
            onUpdatePulseField={updateDraftPulseField}
        />
    );

    return (
        <div className={wsStyles.editBody}>
            <div className="row m-0 p-0 ps-5">
                <div className="col-sm-12 col-md-1 m-0 p-0" />
                <div className="col-sm-12 col-md-6 m-0 p-0">
                    <div className={`${wsStyles.formBodies} `}>
                        {step === 1 && renderStep1()}
                        {step === 2 && renderStep2()}
                        {step === 3 && renderStep3()}
                        {step === 4 && renderStep4()}
                        {step === 5 && renderStep5()}
                        {step === 6 && renderStep6()}

                        {error ? (
                            <p className={styles.error} role="alert">
                                {error}
                            </p>
                        ) : null}

                        <div className={styles.actions}>
                            {step === 2 ? (
                                <div className={styles.step2Actions} style={{ maxWidth: '550px' }}>
                                    <button type="button" className={`${wsStyles.submitButton} ${submitting ? styles.submitDisabled : ''}`} disabled={submitting} onClick={goNext}>
                                        Continue
                                    </button>
                                    <div className={styles.step2Secondary}>
                                        <button type="button" className={styles.stepActionLink} onClick={handleAddAllFeatures}>
                                            Add all
                                        </button>
                                        <button type="button" className={styles.stepActionLink} onClick={handleRemoveAllOptionalFeatures}>
                                            Remove all
                                        </button>
                                    </div>
                                </div>
                            ) : step === 3 ? (
                                <div className={styles.step2Actions} style={{ maxWidth: '500px' }}>
                                    <button type="button" className={`${wsStyles.submitButton} ${submitting ? styles.submitDisabled : ''}`} disabled={submitting} onClick={goNext}>
                                        Continue
                                    </button>
                                    <button type="button" className={styles.stepActionLink} onClick={goBack}>
                                        Back
                                    </button>
                                </div>
                            ) : step === 4 ? (
                                <div className={styles.step2Actions} style={{ maxWidth: '700px' }}>
                                    <button type="button" className={styles.stepActionLink} onClick={goBack}>
                                        Back
                                    </button>
                                    <button type="button" className={`${wsStyles.submitButton} ${submitting ? styles.submitDisabled : ''}`} disabled={submitting} onClick={goNext}>
                                        Continue
                                    </button>
                                </div>
                            ) : step === 5 ? (
                                hasOrgProfileChanges ? (
                                    <div className={styles.step2Actions} style={{ maxWidth: '700px' }}>
                                        <button type="button" className={styles.stepActionLink} disabled={orgProfileSaving} onClick={handleOrgProfileCancel}>
                                            Revert
                                        </button>
                                        <button
                                            type="button"
                                            className={`${wsStyles.submitButton} ${orgProfileSaving ? styles.submitDisabled : ''}`}
                                            disabled={orgProfileSaving}
                                            onClick={saveOrgProfile}>
                                            {orgProfileSaving ? 'Saving…' : 'Save'}
                                        </button>
                                    </div>
                                ) : (
                                    <div className={styles.step2Actions} style={{ maxWidth: '700px' }}>
                                        <button type="button" className={styles.stepActionLink} onClick={goBack}>
                                            Back
                                        </button>
                                        <button type="button" className={`${wsStyles.submitButton} ${submitting ? styles.submitDisabled : ''}`} disabled={submitting} onClick={goToStep6}>
                                            Continue
                                        </button>
                                    </div>
                                )
                            ) : step === 6 ? (
                                hasPulseChanges ? (
                                    <div className={styles.step2Actions} style={{ maxWidth: '700px' }}>
                                        <button type="button" className={styles.stepActionLink} disabled={pulseProfileSaving} onClick={handlePulseProfileCancel}>
                                            Revert
                                        </button>
                                        <button
                                            type="button"
                                            className={`${wsStyles.submitButton} ${pulseProfileSaving ? styles.submitDisabled : ''}`}
                                            disabled={pulseProfileSaving}
                                            onClick={savePulseProfile}>
                                            {pulseProfileSaving ? 'Saving…' : 'Save'}
                                        </button>
                                    </div>
                                ) : (
                                    <div className={styles.step2Actions} style={{ maxWidth: '700px' }}>
                                        <button type="button" className={styles.stepActionLink} onClick={goBack}>
                                            Back
                                        </button>
                                        <button type="button" className={styles.stepActionLink} disabled={submitting} onClick={handleFinalSubmit}>
                                            {submitting ? 'Finishing…' : 'Skip for now'}
                                        </button>
                                    </div>
                                )
                            ) : (
                                <button
                                    type="button"
                                    className={`${wsStyles.submitButton} ${step === 1 && !canContinueStep1 ? styles.submitDisabled : ''}`}
                                    disabled={step === 1 ? !canContinueStep1 : false}
                                    onClick={goNext}>
                                    Continue
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WorkspaceSetupWizard;
